import { ReactNode, useState } from 'react';

import { isEqual } from 'lodash-es';
import styled from 'styled-components';

import { IconCheckmark } from '@components/icons/checkmark';
import { Menu } from '@components/menu';
import { MenuItem, MenuProps } from '@components/menu/menu';
import { SearchBox } from '@components/search-box';

type PopoverSelectItem<T> = {
  value: T;
  label: string;
  icon?: JSX.Element;
  helpText?: string;
};

export type PopoverSelectProps<T = string> = Pick<
  MenuProps,
  'placement' | 'width' | 'maxHeight'
> & {
  defaultValue?: Array<PopoverSelectItem<T>['value']>;
  trigger: ReactNode;
  items: Array<PopoverSelectItem<T>>;
  clear?: { label: string; icon: JSX.Element };
  isMulti?: boolean;
  searchPlaceholder?: string;
  onChange(selected: T[]): void;
  clearSelected?: boolean;
};

export const PopoverSelect = <T extends any = string>({
  trigger,
  placement = 'left-start',
  items,
  defaultValue = [],
  clear,
  isMulti = false,
  searchPlaceholder,
  onChange,
  clearSelected = false,
  width,
  maxHeight,
}: PopoverSelectProps<T>) => {
  const [selected, setSelected] = useState(defaultValue);
  const [search, setSearch] = useState('');

  const updateSelected = (newSelected: T[]) => {
    setSelected(clearSelected ? [] : newSelected);
    onChange(newSelected);
  };

  const handleSelect = (value: T) => {
    const filtered = selected.filter((s) => !isEqual(s, value));
    const isSelected = filtered.length !== selected.length;
    if (isSelected) {
      updateSelected(filtered);
    } else {
      updateSelected(selected.concat([value]));
    }
  };

  const selectedIcon = <IconCheckmark color="purple50" />;
  const noneSelected = selected.length === 0;

  return (
    <Menu
      trigger={TriggerPlaceholder}
      triggerProps={{ children: trigger }}
      placement={placement}
      width={width}
      maxHeight={maxHeight}
    >
      <SearchBox
        placeholder={searchPlaceholder}
        onChange={(value) => setSearch(value.toLowerCase())}
        defaultValue={search}
        stopPropagation
      />
      <Menu.Separator />
      {clear && (
        <MenuItem
          {...clear}
          onSelect={() => {
            if (!noneSelected) {
              updateSelected([]);
            }
          }}
          iconRight={noneSelected ? selectedIcon : null}
        />
      )}
      {items.map((item, index) => {
        if (search && !item.label.toLowerCase().includes(search)) {
          return null;
        }
        const isSelected = selected.some((value) => isEqual(item.value, value));
        return (
          <MenuItem
            key={`${item.label}-${index}`}
            label={item.label}
            selectionType={isMulti ? 'open' : 'close'}
            onSelect={() => handleSelect(item.value)}
            icon={item.icon}
            iconRight={isSelected ? selectedIcon : null}
            helpText={item.helpText}
          />
        );
      })}
    </Menu>
  );
};

const TriggerPlaceholder = styled.div({ cursor: 'pointer' });
